import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import AcceptInvite from './pages/AcceptInvite';
import SetPassword from './pages/SetPassword';
import EmployeeDashboard from './pages/EmployeeDashboard';
import AdminDashboard from './pages/AdminDashboard';
import HealthcareCandidates from './pages/HealthcareCandidates';
import RecordVoice from './pages/RecordVoice';
import ReportHistory from './pages/ReportHistory';
import DayResult from './pages/DayResult';
import Consult from './pages/Consult';
import ConditionScore from './pages/ConditionScore';
import AdminPrompts from './pages/AdminPrompts';
import AdminPromptEdit from './pages/AdminPromptEdit';
import AdminReports from './pages/AdminReports';
import AdminWeeklyReports from './pages/AdminWeeklyReports';
import AdminSettings from './pages/AdminSettings';
import AdminUsers from './pages/AdminUsers';
import AdminConsentDocuments from './pages/AdminConsentDocuments';
import AdminConsentDocumentEdit from './pages/AdminConsentDocumentEdit';
import AdminOrganizationMode from './pages/AdminOrganizationMode';
import AdminDataRequests from './pages/AdminDataRequests';
import AdminScopes from './pages/AdminScopes';
import AdminWebhooks from './pages/AdminWebhooks';
import AdminUsersImport from './pages/AdminUsersImport';
import AdminUsersBulkDepartment from './pages/AdminUsersBulkDepartment';
import AdminDepartments from './pages/AdminDepartments';
import AdminAuditLogs from './pages/AdminAuditLogs';
import HealthcarePersonDetail from './pages/HealthcarePersonDetail';
import HealthcareInterview from './pages/HealthcareInterview';
import Settings from './pages/Settings';
import SettingsConsent from './pages/SettingsConsent';
import ConsentOnboarding from './pages/ConsentOnboarding';
import SettingsNotifications from './pages/SettingsNotifications';
import SettingsDataDeletion from './pages/SettingsDataDeletion';
import Notifications from './pages/Notifications';
import Disclaimer from './components/Disclaimer';
import HealthcareModeGate from './components/HealthcareModeGate';

function RoleBasedRedirect() {
  const { user } = useAuth();
  const to = user?.role === 'admin' ? '/admin' : user?.role === 'healthcare' ? '/healthcare' : '/dashboard';
  return <Navigate to={to} replace />;
}

function PrivateRoute({
  children,
  roles,
}: {
  children: React.ReactNode;
  roles?: string[];
}) {
  const { t } = useTranslation();
  const { user, isReady, accessToken, sessionExpired } = useAuth();
  if (!isReady) return <div className="loading-screen">{t('common.loading')}</div>;
  if (!accessToken || !user) {
    return <Navigate to="/login" replace state={sessionExpired ? { sessionExpired: true } : undefined} />;
  }
  if (roles && roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<RoleBasedRedirect />} />
        <Route
          path="dashboard"
          element={
            <PrivateRoute roles={['employee']}>
              <EmployeeDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="record"
          element={
            <PrivateRoute roles={['employee']}>
              <RecordVoice />
            </PrivateRoute>
          }
        />
        <Route path="dashboard/history" element={<PrivateRoute roles={['employee']}><ReportHistory /></PrivateRoute>} />
        <Route path="dashboard/history/:date" element={<PrivateRoute roles={['employee']}><DayResult /></PrivateRoute>} />
        <Route path="dashboard/consult" element={<PrivateRoute roles={['employee']}><Consult /></PrivateRoute>} />
        <Route path="dashboard/score" element={<PrivateRoute roles={['employee']}><ConditionScore /></PrivateRoute>} />
        <Route path="dashboard/settings" element={<PrivateRoute roles={['employee']}><Settings /></PrivateRoute>} />
        <Route path="dashboard/onboarding" element={<PrivateRoute roles={['employee']}><ConsentOnboarding /></PrivateRoute>} />
        <Route path="dashboard/settings/consent" element={<PrivateRoute roles={['employee']}><SettingsConsent /></PrivateRoute>} />
        <Route path="dashboard/settings/notifications" element={<PrivateRoute roles={['employee']}><SettingsNotifications /></PrivateRoute>} />
        <Route path="dashboard/settings/deletion" element={<PrivateRoute roles={['employee']}><SettingsDataDeletion /></PrivateRoute>} />
        <Route path="dashboard/notifications" element={<PrivateRoute roles={['employee']}><Notifications /></PrivateRoute>} />
        <Route
          path="admin"
          element={
            <PrivateRoute roles={['admin']}>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
        <Route path="admin/prompts" element={<PrivateRoute roles={['admin']}><AdminPrompts /></PrivateRoute>} />
        <Route path="admin/prompts/:id" element={<PrivateRoute roles={['admin']}><AdminPromptEdit /></PrivateRoute>} />
        <Route path="admin/users" element={<PrivateRoute roles={['admin']}><AdminUsers /></PrivateRoute>} />
        <Route path="admin/users/import" element={<PrivateRoute roles={['admin']}><AdminUsersImport /></PrivateRoute>} />
        <Route path="admin/users/bulk-department" element={<PrivateRoute roles={['admin']}><AdminUsersBulkDepartment /></PrivateRoute>} />
        <Route path="admin/reports" element={<PrivateRoute roles={['admin']}><AdminReports /></PrivateRoute>} />
        <Route path="admin/weekly-reports" element={<PrivateRoute roles={['admin']}><AdminWeeklyReports /></PrivateRoute>} />
        <Route path="admin/settings" element={<PrivateRoute roles={['admin']}><AdminSettings /></PrivateRoute>} />
        <Route path="admin/consent" element={<PrivateRoute roles={['admin']}><AdminConsentDocuments /></PrivateRoute>} />
        <Route path="admin/consent/new" element={<PrivateRoute roles={['admin']}><AdminConsentDocumentEdit /></PrivateRoute>} />
        <Route path="admin/consent/:id" element={<PrivateRoute roles={['admin']}><AdminConsentDocumentEdit /></PrivateRoute>} />
        <Route path="admin/organization-mode" element={<PrivateRoute roles={['admin']}><AdminOrganizationMode /></PrivateRoute>} />
        <Route path="admin/data-requests" element={<PrivateRoute roles={['admin']}><AdminDataRequests /></PrivateRoute>} />
        <Route path="admin/admin-scopes" element={<PrivateRoute roles={['admin']}><AdminScopes /></PrivateRoute>} />
        <Route path="admin/webhooks" element={<PrivateRoute roles={['admin']}><AdminWebhooks /></PrivateRoute>} />
        <Route path="admin/departments" element={<PrivateRoute roles={['admin']}><AdminDepartments /></PrivateRoute>} />
        <Route path="admin/audit-logs" element={<PrivateRoute roles={['admin']}><AdminAuditLogs /></PrivateRoute>} />
        <Route
          path="healthcare"
          element={
            <PrivateRoute roles={['healthcare']}>
              <HealthcareModeGate><HealthcareCandidates /></HealthcareModeGate>
            </PrivateRoute>
          }
        />
        <Route path="healthcare/person/:id" element={<PrivateRoute roles={['healthcare']}><HealthcareModeGate><HealthcarePersonDetail /></HealthcareModeGate></PrivateRoute>} />
        <Route path="healthcare/person/:id/interview" element={<PrivateRoute roles={['healthcare']}><HealthcareModeGate><HealthcareInterview /></HealthcareModeGate></PrivateRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Disclaimer />
      <AppRoutes />
    </AuthProvider>
  );
}
